import { metalPanel, gameColors } from '@/shared/ui/game-theme';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Rarity, type LootDrop } from '@/game/types';
import { useLocale } from '@/i18n';
import { RARITY_COLORS, RARITY_GLOW, LOOT_POPUP_DURATION_MS } from '@/shared/constants';

// ---- Types ----

interface LootPopupProps {
  loot: LootDrop | null;
}

// ---- Helpers ----

function rarityLabel(rarity: Rarity): string {
  switch (rarity) {
    case Rarity.Common:    return 'Common';
    case Rarity.Uncommon:  return 'Uncommon';
    case Rarity.Rare:      return 'Rare';
    case Rarity.Epic:      return 'Epic';
    case Rarity.Legendary: return 'Legendary';
    default:               return 'Common';
  }
}

// ---- Component ----

export function LootPopup({ loot }: LootPopupProps) {
  const { locale } = useLocale();
  const [visible, setVisible] = useState(false);
  const [currentLoot, setCurrentLoot] = useState<LootDrop | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!loot) return;

    if (hideTimer.current !== null) clearTimeout(hideTimer.current);
    setCurrentLoot(loot);
    setVisible(true);

    hideTimer.current = setTimeout(() => {
      setVisible(false);
      setCurrentLoot(null);
      hideTimer.current = null;
    }, LOOT_POPUP_DURATION_MS);

    // Source loot clears earlier than this notification's own lifetime.
    // Only a newer drop or unmount cancels its pending expiry.
  }, [loot, loot?.goldAmount, loot?.rarity, loot?.itemName, loot?.bonusItem?.itemType]);

  useEffect(() => () => {
    if (hideTimer.current !== null) clearTimeout(hideTimer.current);
    hideTimer.current = null;
  }, []);

  if (!visible || !currentLoot) return null;

  const color = RARITY_COLORS[currentLoot.rarity] ?? RARITY_COLORS.common;
  const bonusColor = currentLoot.bonusItem
    ? (RARITY_COLORS[currentLoot.bonusItem.rarity] ?? color)
    : color;

  const containerStyle: CSSProperties = {
    position: 'absolute',
    bottom: 100,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    padding: '12px 24px',
    borderRadius: 12,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    border: `2px solid ${color}`,
    boxShadow: RARITY_GLOW[currentLoot.rarity] ?? `0 0 20px ${color}66`,
    zIndex: 400,
    pointerEvents: 'none',
    animation: 'loot-slide-in 0.4s ease-out',
    overflow: 'hidden',
    ...metalPanel,
  };

  const nameStyle: CSSProperties = {
    fontSize: 16,
    fontWeight: 700,
    color,
    textShadow: '0 1px 2px #131b1d',
    textAlign: 'center',
  };

  const rarityTagStyle: CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color,
    opacity: 0.8,
    textTransform: 'uppercase',
    letterSpacing: 1,
  };

  const goldStyle: CSSProperties = {
    fontSize: 14,
    fontWeight: 700,
    color: gameColors.gold,
    textShadow: '0 1px 2px #131b1d',
  };

  return (
    <>
      <style>{`
        @keyframes loot-slide-in {
          0%   { transform: translateX(-50%) translateY(40px); opacity: 0; }
          100% { transform: translateX(-50%) translateY(0);    opacity: 1; }
        }
        @keyframes legendary-rays {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div style={containerStyle} className="th-loot-popup">

        <span style={rarityTagStyle}>{locale === 'ru' ? ({ common: 'Обычный', uncommon: 'Необычный', rare: 'Редкий', epic: 'Эпический', legendary: 'Легендарный' }[currentLoot.rarity] ?? 'Обычный') : rarityLabel(currentLoot.rarity)}</span>

        {currentLoot.itemName && !currentLoot.bonusItem && (
          <span style={nameStyle} className="th-loot-name">{currentLoot.itemName}</span>
        )}

        {currentLoot.goldAmount > 0 && (
          <span style={goldStyle} className="th-loot-gold">+{currentLoot.goldAmount} {locale === 'ru' ? 'монет' : 'gold'}</span>
        )}

        {currentLoot.bonusItem && (
          <span
            className="th-loot-name"
            style={{
              ...nameStyle,
              color: bonusColor,
              textShadow: '0 1px 2px #131b1d',
            }}
          >
            {currentLoot.bonusItem.itemName ??
              (currentLoot.bonusItem.itemType === 'main_hand' || currentLoot.bonusItem.itemType === 'weapon'
                ? 'Weapon drop'
                : currentLoot.bonusItem.itemType === 'chest' || currentLoot.bonusItem.itemType === 'armor'
                  ? 'Armor drop'
                  : 'Equipment drop')}
          </span>
        )}
      </div>
    </>
  );
}
